import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import type { SelectOption } from '../../../components/ui';
import { Button, Label, SegmentedControl, Select, Switch, Textarea, TextInput } from '../../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../../components/ui/Modal';
import { cn } from '../../../lib/utils';
import {
  FilterIcon,
  InfoIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
  TableIcon,
} from '../../../components/ui/Icons';
import { upsertReferenceTemplate } from '../../../services/mutations';

export type ReferenceTemplateModuleId = 'supplies' | 'spares';

type TemplateRow = {
  id: number | null;
  entityType: string;
  pattern: string;
  sequenceScope: string;
  sequenceDigits: number;
  isActive: boolean;
  allowManualReferenceEdit?: boolean;
  description: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type ModuleConfig = {
  id: ReferenceTemplateModuleId;
  label: string;
  adminEyebrow: string;
  apiPath: string;
  caseNoun: string;
  categorySource: string;
  caseRefSource: string;
  entityLabels: Record<string, string>;
  entityOrder: readonly string[];
  badgeClasses: Record<string, string>;
  upsert: (
    entityType: string,
    body: {
      pattern: string;
      sequenceScope: string;
      sequenceDigits: number;
      isActive: boolean;
      allowManualReferenceEdit: boolean;
      description: string | null;
    },
  ) => Promise<unknown>;
};

const SUPPLIES_CONFIG: ModuleConfig = {
  id: 'supplies',
  label: 'Supplies',
  adminEyebrow: 'Supplies · Administration',
  apiPath: '/reference-templates?moduleId=supplies',
  caseNoun: 'supply case',
  categorySource:
    'First selected store category (`categoryIds[0]`): abbreviation or letters/digits from name. `{category_abrv:N}` keeps the first N characters (N = 1–12).',
  caseRefSource:
    'Parent supply case reference (`SupplyCase.ref`), sanitized; fallback `C{id}` or CASE (display capped at 10 characters).',
  entityLabels: {
    SUPPLY_CASE: 'Supply case',
    SUPPLY_REQUISITION: 'Supply requisition',
    SUPPLIER_ENQUIRY: 'Supplier enquiry (RFQ)',
    SUPPLIER_QUOTATION: 'Supplier quotation',
    QUOTATION_EVALUATION: 'Quotation evaluation',
    SUPPLY_PURCHASE_ORDER: 'Purchase order',
    SUPPLY_DELIVERY: 'Delivery',
    SUPPLY_INVOICE: 'Invoice',
  },
  entityOrder: [
    'SUPPLY_CASE',
    'SUPPLY_REQUISITION',
    'SUPPLIER_ENQUIRY',
    'SUPPLIER_QUOTATION',
    'QUOTATION_EVALUATION',
    'SUPPLY_PURCHASE_ORDER',
    'SUPPLY_DELIVERY',
    'SUPPLY_INVOICE',
  ],
  badgeClasses: {
    SUPPLY_CASE: 'bg-slate-500/15 text-slate-800 dark:text-slate-200 ring-1 ring-slate-500/25',
    SUPPLY_REQUISITION: 'bg-sky-500/15 text-sky-900 dark:text-sky-100 ring-1 ring-sky-500/25',
    SUPPLIER_ENQUIRY: 'bg-violet-500/15 text-violet-900 dark:text-violet-100 ring-1 ring-violet-500/25',
    SUPPLIER_QUOTATION: 'bg-fuchsia-500/15 text-fuchsia-900 dark:text-fuchsia-100 ring-1 ring-fuchsia-500/25',
    QUOTATION_EVALUATION: 'bg-amber-500/15 text-amber-900 dark:text-amber-100 ring-1 ring-amber-500/25',
    SUPPLY_PURCHASE_ORDER: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 ring-1 ring-emerald-500/25',
    SUPPLY_DELIVERY: 'bg-cyan-500/15 text-cyan-900 dark:text-cyan-100 ring-1 ring-cyan-500/25',
    SUPPLY_INVOICE: 'bg-rose-500/15 text-rose-900 dark:text-rose-100 ring-1 ring-rose-500/25',
  },
  upsert: upsertReferenceTemplate,
};

const SPARES_CONFIG: ModuleConfig = {
  id: 'spares',
  label: 'Spares',
  adminEyebrow: 'Spares · Administration',
  apiPath: '/reference-templates?moduleId=spares',
  caseNoun: 'spare case',
  categorySource:
    'First selected spare category (`categoryIds[0]`): abbreviation or letters/digits from name. `{category_abrv:N}` keeps the first N characters (N = 1–12).',
  caseRefSource:
    'Parent spare case reference (`SpareCase.ref`), sanitized; fallback `C{id}` or CASE (display capped at 10 characters).',
  entityLabels: {
    SPARE_CASE: 'Spare case',
    SPARE_REQUISITION: 'Spare requisition',
    SPARE_SUPPLIER_ENQUIRY: 'Supplier enquiry (RFQ)',
    SPARE_SUPPLIER_QUOTATION: 'Supplier quotation',
    SPARE_QUOTATION_EVALUATION: 'Quotation evaluation',
    SPARE_PURCHASE_ORDER: 'Purchase order',
    SPARE_DELIVERY: 'Delivery',
    SPARE_INVOICE: 'Invoice',
  },
  entityOrder: [
    'SPARE_CASE',
    'SPARE_REQUISITION',
    'SPARE_SUPPLIER_ENQUIRY',
    'SPARE_SUPPLIER_QUOTATION',
    'SPARE_QUOTATION_EVALUATION',
    'SPARE_PURCHASE_ORDER',
    'SPARE_DELIVERY',
    'SPARE_INVOICE',
  ],
  badgeClasses: {
    SPARE_CASE: 'bg-slate-500/15 text-slate-800 dark:text-slate-200 ring-1 ring-slate-500/25',
    SPARE_REQUISITION: 'bg-sky-500/15 text-sky-900 dark:text-sky-100 ring-1 ring-sky-500/25',
    SPARE_SUPPLIER_ENQUIRY: 'bg-violet-500/15 text-violet-900 dark:text-violet-100 ring-1 ring-violet-500/25',
    SPARE_SUPPLIER_QUOTATION: 'bg-fuchsia-500/15 text-fuchsia-900 dark:text-fuchsia-100 ring-1 ring-fuchsia-500/25',
    SPARE_QUOTATION_EVALUATION: 'bg-amber-500/15 text-amber-900 dark:text-amber-100 ring-1 ring-amber-500/25',
    SPARE_PURCHASE_ORDER: 'bg-emerald-500/15 text-emerald-900 dark:text-emerald-100 ring-1 ring-emerald-500/25',
    SPARE_DELIVERY: 'bg-cyan-500/15 text-cyan-900 dark:text-cyan-100 ring-1 ring-cyan-500/25',
    SPARE_INVOICE: 'bg-rose-500/15 text-rose-900 dark:text-rose-100 ring-1 ring-rose-500/25',
  },
  upsert: upsertReferenceTemplate,
};

const MODULE_CONFIGS: Record<ReferenceTemplateModuleId, ModuleConfig> = {
  supplies: SUPPLIES_CONFIG,
  spares: SPARES_CONFIG,
};

const MODULE_OPTIONS: ReferenceTemplateModuleId[] = ['supplies', 'spares'];

function parseModuleId(raw: string | null): ReferenceTemplateModuleId {
  return raw === 'spares' ? 'spares' : 'supplies';
}

const DIGIT_SELECT_OPTIONS: SelectOption[] = [
  { value: 4, label: '4 — 0001, 0002, …' },
  { value: 3, label: '3 — 001, 002, …' },
];

type PlaceholderInsert = { label: string; token: string };

type PlaceholderRow = {
  id: string;
  inserts: PlaceholderInsert[];
  source: string;
};

function buildPlaceholderRows(config: ModuleConfig): PlaceholderRow[] {
  return [
    {
      id: 'vessel',
      inserts: [
        { label: '{vessel:N}', token: '{vessel:3}' },
        { label: '{vessel_abrv}', token: '{vessel_abrv}' },
        { label: '{vessel_abrv:N}', token: '{vessel_abrv:4}' },
      ],
      source:
        '`{vessel:N}` — first N characters (1–12) from vessel name (`Vessel.vesselName`), sanitized and padded. `{vessel_abrv}` uses `Vessel.vesselAbrv` when set; otherwise falls back to cleaned name (display capped at 5 chars). `{vessel_abrv:N}` keeps the first N characters (N = 1–12).',
    },
    {
      id: 'category',
      inserts: [
        { label: '{category_abrv}', token: '{category_abrv}' },
        { label: '{category_abrv:N}', token: '{category_abrv:4}' },
      ],
      source: config.categorySource,
    },
    {
      id: 'year',
      inserts: [
        { label: '{year}', token: '{year}' },
        { label: '{yy}', token: '{yy}' },
        { label: '{mm}', token: '{mm}' },
      ],
      source:
        'From the document date (UTC; falls back to current UTC date): `{year}` is the full four-digit year (e.g. 2026), `{yy}` is the last two digits (e.g. 26), `{mm}` is the month zero-padded (`01`–`12`).',
    },
    {
      id: 'seq',
      inserts: [
        { label: '{seq3}', token: '{seq3}' },
        { label: '{seq4}', token: '{seq4}' },
      ],
      source:
        'Counter for this template’s sequence scope (see Scope below). `{seq3}` is always three digits (`001`, `002`, …). `{seq4}` is always four digits (`0001`, `0002`, …). Legacy `{seq}` follows the Digits setting.',
    },
    {
      id: 'case_ref',
      inserts: [{ label: '{case_ref}', token: '{case_ref}' }],
      source: config.caseRefSource,
    },
  ];
}

function entityBadgeClass(config: ModuleConfig, entityType: string): string {
  const first = config.entityOrder[0];
  return config.badgeClasses[entityType] ?? config.badgeClasses[first] ?? '';
}

function stageStep(config: ModuleConfig, entityType: string): number {
  const i = config.entityOrder.indexOf(entityType);
  return i >= 0 ? i + 1 : 0;
}

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleId = parseModuleId(searchParams.get('moduleId'));
  const config = MODULE_CONFIGS[moduleId];

  const setModuleId = useCallback(
    (next: ReferenceTemplateModuleId) => {
      const params = new URLSearchParams(searchParams);
      params.set('moduleId', next);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useLayoutEffect(() => {
    if (!searchParams.get('moduleId')) {
      const params = new URLSearchParams(searchParams);
      params.set('moduleId', 'supplies');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Close editor when switching Supplies ↔ Spares so entity types never mix.
  React.useEffect(() => {
    setModalOpen(false);
    setEditing(null);
  }, [moduleId]);

  const scopeLabels = useMemo(
    (): Record<string, string> => ({
      GLOBAL_YEAR: 'Global per calendar year',
      VESSEL_YEAR: 'Per vessel per year',
      CASE_YEAR: `Per ${config.caseNoun} per year`,
    }),
    [config.caseNoun],
  );

  const scopeSelectOptions = useMemo(
    (): SelectOption[] =>
      Object.entries(scopeLabels).map(([value, label]) => ({
        value,
        label,
      })),
    [scopeLabels],
  );

  const placeholderRows = useMemo(() => buildPlaceholderRows(config), [config]);

  const { data, error, isLoading, mutate } = useSWR<TemplateRow[]>(config.apiPath, fetcher);
  const rows = useMemo(() => data ?? [], [data]);

  const activeConfigured = useMemo(
    () => rows.filter((r) => r.isActive && (r.pattern ?? '').trim().length > 0).length,
    [rows],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TemplateRow | null>(null);
  const [pattern, setPattern] = useState('');
  const [sequenceScope, setSequenceScope] = useState('VESSEL_YEAR');
  const [sequenceDigits, setSequenceDigits] = useState(4);
  const [isActive, setIsActive] = useState(true);
  const [description, setDescription] = useState('');
  const [allowManualReferenceEdit, setAllowManualReferenceEdit] = useState(true);
  const [saving, setSaving] = useState(false);

  const patternRef = useRef<HTMLTextAreaElement>(null);
  const pendingPatternCaretRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const pos = pendingPatternCaretRef.current;
    if (pos === null) return;
    pendingPatternCaretRef.current = null;
    const el = patternRef.current;
    if (!el) return;
    el.focus();
    const safe = Math.min(pos, el.value.length);
    el.setSelectionRange(safe, safe);
  }, [pattern]);

  const insertPlaceholderToken = useCallback((token: string) => {
    setPattern((prev) => {
      const el = patternRef.current;
      let start = prev.length;
      let end = prev.length;
      if (el && document.activeElement === el) {
        start = el.selectionStart ?? 0;
        end = el.selectionEnd ?? 0;
      }
      const next = prev.slice(0, start) + token + prev.slice(end);
      pendingPatternCaretRef.current = start + token.length;
      return next;
    });
  }, []);

  const openEdit = useCallback((row: TemplateRow) => {
    setEditing(row);
    setPattern(row.pattern ?? '');
    setSequenceScope(row.sequenceScope ?? 'VESSEL_YEAR');
    setSequenceDigits(row.sequenceDigits === 3 ? 3 : 4);
    setIsActive(row.isActive ?? false);
    setAllowManualReferenceEdit(row.allowManualReferenceEdit !== false);
    setDescription(row.description ?? '');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditing(null);
  }, []);

  const save = useCallback(async () => {
    if (!editing?.entityType) return;
    setSaving(true);
    try {
      await config.upsert(editing.entityType, {
        pattern: pattern.trim(),
        sequenceScope,
        sequenceDigits,
        isActive,
        allowManualReferenceEdit,
        description: description.trim() || null,
      });
      await mutate();
      showNotification('Template saved.', 'success');
      closeModal();
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'response' in e ? (e as any).response?.data?.message : null;
      showNotification(msg || 'Could not save template.', 'error');
    } finally {
      setSaving(false);
    }
  }, [
    closeModal,
    config,
    description,
    allowManualReferenceEdit,
    editing?.entityType,
    isActive,
    mutate,
    pattern,
    sequenceDigits,
    sequenceScope,
    showNotification,
  ]);

  return (
    <div
      className={cn(
        'min-h-[60vh] w-full min-w-0 px-4 sm:px-6 lg:px-8 py-8 space-y-10',
        colors.background,
      )}
    >
      <header
        className={cn(
          'relative overflow-hidden rounded-2xl border shadow-sm',
          colors.border,
          colors.surface,
        )}
      >
        <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" aria-hidden />
        <div className="p-6 sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex gap-4 max-w-2xl">
              <div
                className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1',
                  colors.primaryFg,
                  isDark ? 'bg-white/[0.06] ring-white/10' : 'bg-black/[0.04] ring-black/10',
                )}
              >
                <TableIcon className="w-6 h-6" aria-hidden />
              </div>
              <div>
                <p className={cn('text-xs font-semibold uppercase tracking-wider', colors.textMuted)}>
                  {config.adminEyebrow}
                </p>
                <h1 className={cn('mt-1 text-2xl sm:text-3xl font-bold tracking-tight', colors.text)}>
                  Reference templates
                </h1>
                <p className={cn('mt-2 text-sm leading-relaxed', colors.textSecondary)}>
                  Define how automatic document <strong className={cn('font-medium', colors.text)}>ref</strong> values are
                  built when users leave reference blank on create. Each segment supports{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{name:N}'}</code> for the first N characters (
                  <code className={cn('font-mono text-xs', colors.text)}>{'{vessel:3}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{vessel_abrv}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{category_abrv:4}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{yy}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{mm}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{seq4}'}</code>,{' '}
                  <code className={cn('font-mono text-xs', colors.text)}>{'{case_ref}'}</code>, etc.).
                </p>
                <ol
                  className={cn(
                    'mt-4 grid gap-2 text-xs sm:text-sm sm:grid-cols-2 lg:grid-cols-4',
                    colors.textSecondary,
                  )}
                >
                  {[
                    '1 · Edit stage row',
                    '2 · Pattern & placeholders',
                    '3 · Sequence scope',
                    '4 · Activate',
                  ].map((step) => (
                    <li
                      key={step}
                      className={cn(
                        'rounded-lg px-3 py-2 border',
                        colors.border,
                        colors.surfaceHover,
                      )}
                    >
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
            <SegmentedControl
              aria-label="Module"
              size="sm"
              value={moduleId}
              onChange={setModuleId}
              options={MODULE_OPTIONS.map((id) => ({
                id,
                label: MODULE_CONFIGS[id].label,
              }))}
            />
          </div>
        </div>
      </header>

      <section className="space-y-4" aria-labelledby="ref-templates-heading">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 id="ref-templates-heading" className={cn('text-lg font-semibold', colors.text)}>
              Pipeline stages
            </h2>
            <p className={cn('text-sm mt-0.5 max-w-2xl', colors.textSecondary)}>
              One rule per stage. Only <strong className={cn('font-medium', colors.text)}>active</strong> templates with a
              non-empty pattern auto-fill refs. Multiple categories: the{' '}
              <strong className={cn('font-medium', colors.text)}>first selected</strong> id drives{' '}
              <code className="font-mono text-[11px]">{'{category_abrv}'}</code> /{' '}
              <code className="font-mono text-[11px]">{'{category_abrv:N}'}</code>. Output max{' '}
              <strong className={cn('font-medium', colors.text)}>30</strong> characters.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={cn(
                'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border',
                colors.border,
                colors.surfaceHover,
                colors.textSecondary,
              )}
            >
              <FilterIcon className="w-3.5 h-3.5 opacity-80" aria-hidden />
              {rows.length} stage{rows.length !== 1 ? 's' : ''}
              <span className="opacity-40" aria-hidden>
                ·
              </span>
              {activeConfigured} active
            </span>
          </div>
        </div>

        {error && (
          <div
            className={cn(
              'flex items-start gap-2 rounded-xl border px-4 py-3 text-sm',
              colors.border,
              colors.error,
            )}
            role="alert"
          >
            <InfoIcon className="w-5 h-5 shrink-0 opacity-80" aria-hidden />
            <span>Could not load templates. Refresh the page or check your connection.</span>
          </div>
        )}

        <div
          className={cn(
            'rounded-2xl border shadow-sm overflow-hidden',
            colors.border,
            colors.surface,
          )}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                  <th className="px-4 py-3 font-semibold">Stage</th>
                  <th className={cn('px-4 py-3 font-semibold whitespace-nowrap')}># · Entity</th>
                  <th className="px-4 py-3 font-semibold min-w-[12rem]">Pattern</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap hidden md:table-cell">Scope</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Digits</th>
                  <th className="px-4 py-3 font-semibold whitespace-nowrap">Live</th>
                  <th className="px-4 py-3 font-semibold text-right w-[1%]">Actions</th>
                </tr>
              </thead>
              <tbody className={cn('divide-y', colors.divide)}>
                {isLoading && (
                  <tr>
                    <td colSpan={7} className={cn('px-4 py-16 text-center', colors.textSecondary)}>
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin opacity-70"
                          aria-hidden
                        />
                        Loading templates…
                      </span>
                    </td>
                  </tr>
                )}
                {!isLoading &&
                  rows.map((row) => {
                    const configured = (row.pattern ?? '').trim().length > 0;
                    const live = row.isActive && configured;
                    const step = stageStep(config, row.entityType);
                    return (
                      <tr
                        key={row.entityType}
                        className={cn(
                          'transition-colors',
                          colors.interactiveHover,
                          live &&
                            cn(
                              'ring-1 ring-inset',
                              isDark
                                ? 'bg-white/[0.04] ring-white/15'
                                : 'bg-black/[0.03] ring-black/10',
                            ),
                        )}
                      >
                        <td className="px-4 py-3 align-middle">
                          <span
                            className={cn(
                              'inline-flex w-fit max-w-[14rem] items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                              entityBadgeClass(config, row.entityType),
                            )}
                          >
                            {config.entityLabels[row.entityType] ?? row.entityType}
                          </span>
                        </td>
                        <td className={cn('px-4 py-3 align-middle whitespace-nowrap font-mono text-[11px]', colors.textMuted)}>
                          #{step > 0 ? String(step).padStart(2, '0') : '—'} · {row.entityType}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {configured ? (
                            <code
                              className={cn(
                                'block max-w-xl truncate rounded-lg border px-2 py-1.5 font-mono text-xs',
                                colors.border,
                                colors.surfaceHover,
                                colors.text,
                              )}
                              title={row.pattern}
                            >
                              {row.pattern}
                            </code>
                          ) : (
                            <span className={cn('text-xs italic', colors.textMuted)}>Not configured</span>
                          )}
                        </td>
                        <td className={cn('px-4 py-3 align-middle hidden md:table-cell', colors.textSecondary)}>
                          {scopeLabels[row.sequenceScope] ?? row.sequenceScope}
                        </td>
                        <td className={cn('px-4 py-3 align-middle tabular-nums', colors.text)}>{row.sequenceDigits}</td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap">
                          {live ? (
                            <span className={cn('inline-flex items-center gap-1.5', colors.primaryFg)}>
                              <StarIcon filled className="w-4 h-4 shrink-0" aria-hidden />
                              <span className="text-xs font-medium">Active</span>
                            </span>
                          ) : row.isActive && !configured ? (
                            <span
                              className={cn(
                                'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1',
                                colors.warning,
                                isDark ? 'ring-amber-500/30' : 'ring-amber-500/40',
                              )}
                            >
                              Incomplete
                            </span>
                          ) : (
                            <span className={cn('text-xs', colors.textMuted)}>—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right align-middle">
                          <Button
                            type="button"
                            size="icon"
                            variant="outline"
                            title="Edit template"
                            aria-label={`Edit template for ${config.entityLabels[row.entityType] ?? row.entityType}`}
                            onClick={() => openEdit(row)}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={closeModal} size="2xl">
        <ModalHeader>
          <div className="flex flex-col gap-1 pr-8">
            <ModalTitle>
              {editing ? config.entityLabels[editing.entityType] ?? editing.entityType : 'Template'}
            </ModalTitle>
            {editing ? (
              <p className={cn('font-mono text-xs', colors.textMuted)}>
                {editing.entityType}
                {stageStep(config, editing.entityType) > 0 ? (
                  <span
                    className={cn(
                      'ml-2 rounded-md px-2 py-0.5 text-[11px] font-sans font-medium',
                      colors.primary,
                      colors.primaryText,
                    )}
                  >
                    Step {stageStep(config, editing.entityType)}
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
        </ModalHeader>
        <ModalContent>
          <div className="grid gap-8 lg:grid-cols-[11fr_13fr]">
            <div className="min-w-0 space-y-6">
              <section className="space-y-3">
                <div className={cn('flex items-center gap-2 border-b pb-2', colors.border)}>
                  <span
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold',
                      colors.primary,
                      colors.primaryText,
                    )}
                  >
                    1
                  </span>
                  <h3 className={cn('text-sm font-semibold', colors.text)}>Pattern</h3>
                </div>
                <div>
                  <Label htmlFor="pattern">Template string</Label>
                  <Textarea
                    ref={patternRef}
                    id="pattern"
                    className={cn(
                      'mt-1.5 min-h-[104px] resize-y rounded-xl border px-3 py-2.5 text-sm font-mono shadow-sm',
                      colors.border,
                      colors.surface,
                      colors.text,
                    )}
                    rows={4}
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    placeholder="{vessel:3}-{category_abrv}-{year}-{seq4}"
                    showClearButton={false}
                  />
                  <p className={cn('mt-1.5 text-xs leading-relaxed', colors.textMuted)}>
                    Literal characters are copied as-is. Click a placeholder on the right to insert at the cursor. Output
                    trimmed to <strong className={colors.text}>30 characters</strong>.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <div className={cn('flex items-center gap-2 border-b pb-2', colors.border)}>
                  <span
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold',
                      colors.primary,
                      colors.primaryText,
                    )}
                  >
                    2
                  </span>
                  <h3 className={cn('text-sm font-semibold', colors.text)}>Sequence</h3>
                </div>
                <div className="flex flex-col gap-4">
                  <div>
                    <Label htmlFor="scope">Scope</Label>
                    <Select
                      id="scope"
                      className="mt-1.5"
                      placeholder="Select scope"
                      showClearButton={false}
                      options={scopeSelectOptions}
                      value={sequenceScope}
                      onChange={(e) => setSequenceScope(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="digits">
                      Digits <span className={cn('font-normal', colors.textMuted)}>(only when pattern uses {'{seq}'})</span>
                    </Label>
                    <Select
                      id="digits"
                      className="mt-1.5"
                      placeholder="Select digits"
                      showClearButton={false}
                      options={DIGIT_SELECT_OPTIONS}
                      value={sequenceDigits === 3 ? 3 : 4}
                      onChange={(e) => setSequenceDigits(Number(e.target.value) === 3 ? 3 : 4)}
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-3">
                <div className={cn('flex items-center gap-2 border-b pb-2', colors.border)}>
                  <span
                    className={cn(
                      'flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold',
                      colors.primary,
                      colors.primaryText,
                    )}
                  >
                    3
                  </span>
                  <h3 className={cn('text-sm font-semibold', colors.text)}>Behaviour</h3>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <Switch id="active" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    <Label htmlFor="active" className="cursor-pointer">
                      Auto-fill on create when reference is empty
                    </Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      id="manualRef"
                      checked={allowManualReferenceEdit}
                      onChange={(e) => setAllowManualReferenceEdit(e.target.checked)}
                    />
                    <Label htmlFor="manualRef" className="cursor-pointer">
                      Allow users to edit reference on create/edit forms (when off, reference stays template-generated)
                    </Label>
                  </div>
                </div>
                <div>
                  <Label htmlFor="desc">Internal note (optional)</Label>
                  <TextInput
                    id="desc"
                    type="text"
                    className={cn(
                      'mt-1.5 h-10 min-h-[2.5rem] rounded-xl px-3 py-2 shadow-sm',
                      colors.border,
                      colors.surface,
                      colors.text,
                    )}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. Align with vessel provisioning codes"
                    showClearButton={false}
                  />
                </div>
              </section>
            </div>

            <aside
              className={cn(
                'min-w-0 rounded-2xl border p-4 sm:p-5 lg:sticky lg:top-0 lg:self-start lg:max-h-[min(70vh,calc(100vh-12rem))] lg:overflow-y-auto',
                colors.border,
                colors.surfaceHover,
              )}
            >
              <div className="flex items-center gap-2">
                <InfoIcon className={cn('h-4 w-4 shrink-0', colors.textMuted)} aria-hidden />
                <p className={cn('text-xs font-semibold uppercase tracking-wide', colors.text)}>Placeholders</p>
              </div>
              <p className={cn('mt-2 text-xs leading-relaxed', colors.textMuted)}>
                Click a chip to insert into the pattern (at the caret, or append if the pattern field is not focused).
                Replace N in the template when you need a different length.
              </p>
              <ul className="mt-4 space-y-3">
                {placeholderRows.map((prow) => (
                  <li
                    key={prow.id}
                    className={cn(
                      'rounded-xl border p-3 shadow-sm',
                      colors.border,
                      colors.surface,
                    )}
                  >
                    <div className="flex flex-wrap gap-2">
                      {prow.inserts.map((ins) => (
                        <button
                          key={ins.token}
                          type="button"
                          className={cn(
                            'group inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-left transition-colors',
                            colors.border,
                            colors.surfaceHover,
                            colors.interactiveHover,
                            colors.primaryFg,
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current focus-visible:ring-offset-2',
                            isDark ? 'focus-visible:ring-offset-[#121a2b]' : 'focus-visible:ring-offset-white',
                          )}
                          onClick={() => insertPlaceholderToken(ins.token)}
                          title={`Insert ${ins.token}`}
                          aria-label={`Insert ${ins.token} into template pattern`}
                        >
                          <code className="font-mono text-[11px] font-semibold">{ins.label}</code>
                          <span
                            className={cn(
                              'inline-flex items-center justify-center rounded px-1 py-0.5 opacity-70 group-hover:opacity-100',
                              isDark ? 'bg-white/10' : 'bg-black/5',
                            )}
                            aria-hidden
                          >
                            <PlusIcon className="h-3.5 w-3.5" />
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className={cn('mt-2 text-xs leading-snug', colors.textMuted)}>{prow.source}</p>
                  </li>
                ))}
              </ul>
              <p className={cn('mt-4 border-t pt-4 text-xs leading-snug', colors.border, colors.textMuted)}>
                <strong className={colors.text}>Scope</strong> controls how{' '}
                <code className="font-mono text-[11px]">{'{seq3}'}</code> /{' '}
                <code className="font-mono text-[11px]">{'{seq4}'}</code> increments (
                <code className="font-mono text-[11px]">VESSEL_YEAR</code>,{' '}
                <code className="font-mono text-[11px]">CASE_YEAR</code>,{' '}
                <code className="font-mono text-[11px]">GLOBAL_YEAR</code>).
              </p>
            </aside>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <Button type="button" variant="accent" disabled={saving || !pattern.trim()} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save template'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default List;
